import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import IDealStage from '../interfaces/IDealStage';
import IActivity from '../interfaces/IActivity';
import IDeal, { DealShape, IDealState } from '../interfaces/IDeal';
import IDealFieldOption from '../interfaces/IDealFieldOption';
import IDealLabelOption from '../interfaces/IDealLabelOption';
import IDealNote from '../interfaces/IDealNote';
import IDealMailMessage from '../interfaces/IDealMailMessage';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import IPipedriveFileInfo from '../interfaces/IPipedriveFileInfo';
import IPipedriveOrganization from '../interfaces/IPipedriveOrganization';
import { IPipedriveOrganizationLogo } from '../interfaces/IPipedriveOrganizationLogo';
import IPipedrivePerson from '../interfaces/IPipedrivePerson';
import IPipedriveUser from '../interfaces/IPipedriveUser';
import DealFieldsProvider from '../services/dealFieldsProvider';
import LocalStorageService from '../services/localStorageService';
import PipedriveApiService from '../services/pipedriveApiService';

export default class PipedriveStore {
    users: IPipedriveUser[] = [];
    companyDomain: string | null = null;
    activities: IActivity[] = [];
    isActivitiesLoading: ILoadingProps = {};
    organizations: IPipedriveOrganization[] = [];
    isOrganizationsLoading: ILoadingProps = {};
    organizationsLogos: IPipedriveOrganizationLogo[] = [];
    persons: IPipedrivePerson[] = [];
    isPersonsLoading: ILoadingProps = {};
    nonFundOptions: IDealFieldOption[] = [];
    profileOptions: IDealFieldOption[] = [];
    sourcingMethodOptions: IDealFieldOption[] = [];
    labelOptions: IDealLabelOption[] = []
    dealStages: IDealStage[] = [];
    deals: IDeal[] = [];
    isDealsLoading: ILoadingProps = {};
    editedDealId: number | null = null;
    dealIdsNoteLists: { [key: number]: IDealNote[] } = {};
    dealIdsMailMessageLists: { [key: number]: IDealMailMessage[] } = {};
    dealIdsFileLists: { [key: number]: IPipedriveFileInfo[] } = {};

    constructor(
        private pipedriveApiService: PipedriveApiService,
        private localStorageService: LocalStorageService,
        private dealFieldsProvider: DealFieldsProvider,
    ) {
        makeAutoObservable(this);
    }

    setUsers(value: IPipedriveUser[]) {
        this.users = value;
    }

    setCompanyDomain(value: string | null) {
        this.companyDomain = value;
    }

    setActivities(value: IActivity[]) {
        this.activities = value;
    }

    setIsActivitiesLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isActivitiesLoading.fetching = value,
            creating: (value: boolean) => this.isActivitiesLoading.creating = value,
            updating: (value: boolean) => this.isActivitiesLoading.updating = value,
            deleting: (value: boolean) => this.isActivitiesLoading.deleting = value,
        };
    }

    setOrganizations(value: IPipedriveOrganization[]) {
        this.organizations = value;
    }

    setOrganizationsLogos(value: IPipedriveOrganizationLogo[]) {
        this.organizationsLogos = value;
    }

    setIsOrganizationsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isOrganizationsLoading.fetching = value,
            creating: (value: boolean) => this.isOrganizationsLoading.creating = value,
            updating: (value: boolean) => this.isOrganizationsLoading.updating = value,
            deleting: (value: boolean) => this.isOrganizationsLoading.deleting = value,
        };
    }

    setPersons(value: IPipedrivePerson[]) {
        this.persons = value;
    }

    setIsPersonsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isPersonsLoading.fetching = value,
            creating: (value: boolean) => this.isPersonsLoading.creating = value,
            updating: (value: boolean) => this.isPersonsLoading.updating = value,
            deleting: (value: boolean) => this.isPersonsLoading.deleting = value,
        };
    }

    setNonFundOptions(value: IDealFieldOption[]) {
        this.nonFundOptions = value;
    }

    setProfileOptions(value: IDealFieldOption[]) {
        this.profileOptions = value;
    }

    setSourcingMethodOptions(value: IDealFieldOption[]) {
        this.sourcingMethodOptions = value;
    }

    setLabelOptions(value: IDealLabelOption[]) {
        this.labelOptions = value;
    }

    setDealStages(value: IDealStage[]) {
        this.dealStages = value;
    }

    setDeals(value: IDeal[]) {
        this.deals = value;
    }

    setIsDealsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isDealsLoading.fetching = value,
            creating: (value: boolean) => this.isDealsLoading.creating = value,
            updating: (value: boolean) => this.isDealsLoading.updating = value,
            deleting: (value: boolean) => this.isDealsLoading.deleting = value,
        };
    }

    setEditedDealId(value: number | null) {
        this.editedDealId = value;
    }

    setDealNotes(value: IDealNote[], dealId: number) {
        this.dealIdsNoteLists[dealId] = value;
    }

    setDealMailMessages(value: IDealMailMessage[], dealId: number) {
        this.dealIdsMailMessageLists[dealId] = value;
    }

    setDealMailMessageBody(body: string, dealId: number, messageId: number) {
        const message = this.dealIdsMailMessageLists[dealId]?.find(m => m.id === messageId);
        if (message)
            message.body = body;
    }

    setDealFiles(value: IPipedriveFileInfo[], dealId: number) {
        this.dealIdsFileLists[dealId] = value;
    }

    async initialize() {
        this.pipedriveApiService.getUsers()
            .then(pipedriveUsers => {
                this.setUsers(pipedriveUsers);
                const currentPipedriveUser = pipedriveUsers.find(u => u.email === this.localStorageService.getUserEmail());
                if (currentPipedriveUser)
                    this.localStorageService.setPipedriveUserId(currentPipedriveUser.id);
                else
                    toast.warning("No matching Pipedrive account found.");
            });

        this.pipedriveApiService.getCompanyDomain()
            .then(compnayDomain => this.setCompanyDomain(compnayDomain));

        this.pipedriveApiService.getAllOrganizationLogos()
            .then(files => this.setOrganizationsLogos(files));

        this.setIsActivitiesLoading().fetching(true);
        this.pipedriveApiService.getActivities()
            .then(activities => this.setActivities(activities))
            .finally(() => this.setIsActivitiesLoading().fetching(false));

        this.setIsOrganizationsLoading().fetching(true);
        this.pipedriveApiService.getOrganizations()
            .then(organizations => this.setOrganizations(organizations))
            .finally(() => this.setIsOrganizationsLoading().fetching(false));

        this.setIsPersonsLoading().fetching(true);
        this.pipedriveApiService.getPersons()
            .then(persons => this.setPersons(persons))
            .finally(() => this.setIsPersonsLoading().fetching(false));

        this.setIsDealsLoading().fetching(true);
        try {
            const dealFields = await this.pipedriveApiService.getDealFields();
            this.dealFieldsProvider.initialize(dealFields);
            
            this.setNonFundOptions(this.dealFieldsProvider.getNonFundOptions());
            this.setProfileOptions(this.dealFieldsProvider.getDealProfileOptions());
            this.setSourcingMethodOptions(this.dealFieldsProvider.getDealSourcingMethodOptions());
            this.setLabelOptions(this.dealFieldsProvider.getDealLabels());
            this.setDealStages(await this.pipedriveApiService.getDealStages());
            this.setDeals(await this.pipedriveApiService.getDealsByStages(this.dealStages.map(s => s.id)));
        } finally {
            this.setIsDealsLoading().fetching(false);
        }
    }

    async fetchDealNotes(dealId: number): Promise<IDealNote[]> {
        const notes = await this.pipedriveApiService.getDealNotes(dealId);
        this.setDealNotes(notes, dealId);

        return notes;
    }

    async fetchDealMailMessages(dealId: number): Promise<IDealMailMessage[]> {
        const mailMessages = await this.pipedriveApiService.getDealMailMessages(dealId);
        this.setDealMailMessages(mailMessages, dealId);

        return mailMessages;
    }

    async fetchDealMailMessageBody(dealId: number, messageId: number): Promise<void> {
        const message = this.dealIdsMailMessageLists[dealId]?.find(m => m.id === messageId);
        if (!message || message.body !== undefined)
            return;

        const body = await this.pipedriveApiService.getDealMailMessageBody(messageId);
        this.setDealMailMessageBody(body, dealId, messageId);
    }

    async fetchDealFiles(dealId: number): Promise<IPipedriveFileInfo[]> {
        const files = await this.pipedriveApiService.getDealFiles(dealId);
        this.setDealFiles(files, dealId);

        return files;
    }

    async createActivity(dueDate: Date, subject: string, notes: string, userId: number, organizationId: number | null): Promise<number> {
        this.setIsActivitiesLoading().creating(true);
        try {
            const createdActivity = await this.pipedriveApiService.createActivity(dueDate, subject, notes, userId, organizationId);
            this.setActivities([
                ...this.activities,
                createdActivity,
            ]);

            return createdActivity.id;
        } finally {
            this.setIsActivitiesLoading().creating(false);
        }
    }

    async updateActivity(id: number, dueDate: Date, subject: string, notes: string, userId: number, organizationId: number | null) {
        this.setIsActivitiesLoading().updating(true);
        try {
            const updatedActivity = await this.pipedriveApiService.updateActivity(id, dueDate, subject, notes, userId, organizationId);
            this.setActivities([
                ...this.activities.filter(a => a.id !== id),
                updatedActivity,
            ]);
        } finally {
            this.setIsActivitiesLoading().updating(false);
        }
    }

    async markActivityAsDone(id: number) {
        this.setIsActivitiesLoading().updating(true);
        try {
            await this.pipedriveApiService.markActivityAsDone(id);
            this.setActivities(this.activities.filter(a => a.id !== id));
        } finally {
            this.setIsActivitiesLoading().updating(false);
        }
    }

    async deleteActivity(id: number) {
        this.setIsActivitiesLoading().deleting(true);
        try {
            await this.pipedriveApiService.deleteActivity(id);
            this.setActivities(this.activities.filter(a => a.id !== id));
        } finally {
            this.setIsActivitiesLoading().deleting(false);
        }
    }

    async createLabelOption(label: string): Promise<number> {
        const createdLabelOption = await this.pipedriveApiService.createDealLabel(label);

        if (!this.labelOptions.some(o => o.id === createdLabelOption.id)) {
            this.setLabelOptions([
                ...this.labelOptions,
                createdLabelOption,
            ]);
        }

        return createdLabelOption.id;
    }

    async getOrCreatePipedriveOrganizationId(organizationName: string | null): Promise<number | null> {
        if (!organizationName)
            return null;

        this.setIsOrganizationsLoading().creating(true);
        try {
            const organization = await this.pipedriveApiService.getOrCreatePipedriveOrganization(organizationName);

            if (!this.organizations.some(o => o.id === organization.id)) {
                this.setOrganizations([
                    ...this.organizations,
                    organization,
                ]);
            }

            return organization.id;
        } finally {
            this.setIsOrganizationsLoading().creating(false);
        }
    }

    async updateOrganization(organization: IPipedriveOrganization) {
        this.setIsOrganizationsLoading().updating(true);
        try {
            await this.pipedriveApiService.updateOrganization(organization);

            this.setOrganizations([
                ...this.organizations.filter(o => o.id !== organization.id),
                organization,
            ]);
        } finally {
            this.setIsOrganizationsLoading().updating(false);
        }
    }

    async getOrCreatePipedrivePersonId(personName: string | null): Promise<number | null> {
        if (!personName)
            return null;

        this.setIsPersonsLoading().creating(true);
        try {
            const person = await this.pipedriveApiService.getOrCreatePipedrivePerson(personName);

            if (!this.persons.some(p => p.id === person.id)) {
                this.setPersons([
                    ...this.persons,
                    person,
                ]);
            }

            return person.id;
        } finally {
            this.setIsPersonsLoading().creating(false);
        }
    }

    async updateDeal(deal: IDeal) {
        this.setIsDealsLoading().updating(true);
        try {
            const updatedDeal = await this.pipedriveApiService.updateDeal(deal);
            this.setDeals([
                ...this.deals.filter(d => d.id !== deal.id),
                updatedDeal,
            ]);
        } finally {
            this.setIsDealsLoading().updating(false);
        }
    }

    async createDeal(
        dealState: IDealState, contactPersonId: number | null, contactCompanyId: number | null, brokerId: number | null, propertyOwnerId: number | null,
        lat: number, lon: number, dealShapes: DealShape[]
    ): Promise<number> {
        this.setIsDealsLoading().creating(true);
        try {
            const createdDeal = await this.pipedriveApiService.createDeal(dealState, contactPersonId, contactCompanyId, brokerId, propertyOwnerId, lat, lon, dealShapes);
            this.setDeals([
                ...this.deals,
                createdDeal,
            ]);

            return createdDeal.id;
        } finally {
            this.setIsDealsLoading().creating(false);
        }
    }

    async deleteDeal(id: number) {
        this.setIsDealsLoading().deleting(true);
        try {
            await this.pipedriveApiService.deleteDeal(id);
            this.setDeals([
                ...this.deals.filter(d => d.id !== id),
            ]);
        } finally {
            this.setIsDealsLoading().deleting(false);
        }
    }

    async updateTaggedLeaseComp(dealId: number, leaseCompId: number) {
        this.setIsDealsLoading().updating(true);
        try {
            const deal = this.deals.find(d => d.id === dealId)!;
            const isLeaseCompTagged = deal.leaseCompIds.includes(leaseCompId);
            const updatedDeal = await this.pipedriveApiService.updateDeal({
                ...deal,
                leaseCompIds: isLeaseCompTagged
                    ? deal.leaseCompIds.filter(id => id !== leaseCompId)
                    : [
                        ...deal.leaseCompIds,
                        leaseCompId,
                    ],
            });

            this.setDeals([
                ...this.deals.filter(d => d.id !== deal.id),
                updatedDeal,
            ]);
        } finally {
            this.setIsDealsLoading().updating(false);
        }
    }

    async updateTaggedSaleComp(dealId: number, saleCompId: number) {
        this.setIsDealsLoading().updating(true);
        try {
            const deal = this.deals.find(d => d.id === dealId)!;
            const isSaleCompTagged = deal.saleCompIds.includes(saleCompId);
            const updatedDeal = await this.pipedriveApiService.updateDeal({
                ...deal,
                saleCompIds: isSaleCompTagged
                    ? deal.saleCompIds.filter(id => id !== saleCompId)
                    : [
                        ...deal.saleCompIds,
                        saleCompId,
                    ],
            });

            this.setDeals([
                ...this.deals.filter(d => d.id !== deal.id),
                updatedDeal,
            ]);
        } finally {
            this.setIsDealsLoading().updating(false);
        }
    }

    async createDealNote(dealId: number, content: string, userId: number): Promise<void> {
        const dealNote: IDealNote = {
            id: 0,
            dealId,
            userId,
            content,
            orgId: 0,
            createdAt: new Date(),
        };

        const noteResponse = await this.pipedriveApiService.addDealNote(dealNote);
        this.setDealNotes([
            ...this.dealIdsNoteLists[dealId] || [],
            {
                ...dealNote,
                id: noteResponse.id,
                content: noteResponse.content,
                createdAt: noteResponse.createdAt,
            },
        ], dealId);
    }

    async createDealFile(blob: any, name: string, dealId: number): Promise<void> {
        const file = await this.pipedriveApiService.createDealFile(blob, name, dealId);
        this.setDealFiles([
            ...this.dealIdsFileLists[dealId] || [],
            file,
        ], dealId);
    }

    async deleteDealFile(fileId: number, dealId: number): Promise<void> {
        await this.pipedriveApiService.deleteDealFile(fileId);
        this.setDealFiles(this.dealIdsFileLists[dealId].filter(f => f.id !== fileId), dealId);
    }

    getLinkToActivity(id: number, userId: number) {
        return `https://${this.companyDomain}.pipedrive.com/activities/list/user/${userId}?selected=${id}`;
    }

    getLinkToOrganization(id: number) {
        return `https://${this.companyDomain}.pipedrive.com/organization/${id}`;
    }

    getLinkToPerson(id: number) {
        return `https://${this.companyDomain}.pipedrive.com/person/${id}`;
    }

    clear() {
        this.setUsers([]);
        this.setCompanyDomain(null);
        this.setActivities([]);
        this.setOrganizations([]);
        this.setPersons([]);
        this.setNonFundOptions([]);
        this.setProfileOptions([]);
        this.setSourcingMethodOptions([]);
        this.setLabelOptions([]);
        this.setDealStages([]);
        this.setDeals([]);
        this.setEditedDealId(null);
        this.isDealsLoading = {};
        this.dealIdsNoteLists = {};
        this.dealIdsMailMessageLists = {};
        this.dealIdsFileLists = {};
        this.isOrganizationsLoading = {};
        this.isPersonsLoading = {};
        this.isActivitiesLoading = {};
    }
}